import { A } from "@solidjs/router";
import { createSignal, For, Show } from "solid-js";
import { withScheme } from "../../boundaries/url-input.parser";
import { adminGateProps } from "../../store/admin";
import { api } from "../../store/api";
import { createAsyncAction } from "../../ui/asyncAction";
import { FormError } from "../../ui/FormError";
import {
  createPlaygroundWorkspaces,
  invalidateWorkspaces,
} from "./playgroundQueries";
import { VoiceDrawer } from "./VoiceDrawer";

const FLOW = [
  { title: "Add a page", hint: "Paste any URL you want the agent to audit." },
  {
    title: "Choose the setup",
    hint: "Pick the AI instructions and scoring guide for the run.",
  },
  { title: "Run the agent", hint: "It inspects the page and scores it." },
  { title: "Compare", hint: "Put two runs side by side to see what changed." },
];

// People type bare hostnames; the contract wants a real URL. Prefix https://
// when no scheme is present rather than bouncing the input.
function normalizeUrl(raw: string): string {
  return withScheme(raw);
}

/** One workspace overview: its brand voice, the pages under test, and the way
 * in to each page's experiment bench. */
export function PlaygroundWorkspace(props: { clientId: string }) {
  const workspaces = createPlaygroundWorkspaces();
  const workspace = () =>
    workspaces.data?.workspaces.find((w) => w.clientId === props.clientId);

  const [url, setUrl] = createSignal("");
  const action = createAsyncAction();
  const [voiceOpen, setVoiceOpen] = createSignal(false);

  const addUrl = (event: Event) => {
    event.preventDefault();
    if (!url().trim() || action.busy()) return;
    const originUrl = normalizeUrl(url());
    if (!URL.canParse(originUrl)) {
      action.fail(`"${url().trim()}" is not a valid URL`);
      return;
    }
    void action.run(async () => {
      await api.playground.addUrl(props.clientId, { originUrl });
      setUrl("");
      await invalidateWorkspaces();
    });
  };

  return (
    <main class="landing">
      <div class="landing-bar">
        <A href="/playground" class="landing-brand">
          ← Playground
        </A>
        <span class="landing-tag" />
        <A href="/playground/prompts" class="btn">
          Prompt library
        </A>
      </div>
      <div class="landing-main">
        <Show
          when={workspace()}
          fallback={
            <Show
              when={workspaces.data}
              fallback={<div class="loading">Loading workspace…</div>}
            >
              <div class="empty">
                <div class="empty-title">Workspace not found</div>
                <A href="/playground">Back to the playground index</A>
              </div>
            </Show>
          }
        >
          {(current) => (
            <>
              <div class="landing-head-row">
                <div>
                  <h1 class="landing-h1">{current().name}</h1>
                  <p class="landing-sub">
                    <span class="badge">
                      {current().tovVersionId
                        ? "brand voice active"
                        : "no brand voice yet"}
                    </span>{" "}
                    <button
                      type="button"
                      class="link-button"
                      onClick={() => setVoiceOpen(true)}
                    >
                      {current().tovVersionId
                        ? "Edit or replace brand voice"
                        : "Add a brand voice"}
                    </button>
                  </p>
                </div>
              </div>

              <div class="flow-steps">
                <For each={FLOW}>
                  {(step, index) => (
                    <div class="flow-step">
                      <span class="flow-step-num">{index() + 1}</span>
                      <div>
                        <div class="flow-step-title">{step.title}</div>
                        <div class="flow-step-hint">{step.hint}</div>
                      </div>
                    </div>
                  )}
                </For>
              </div>

              <section class="panel">
                <div class="panel-head">
                  <span class="panel-title">Pages under test</span>
                  <form onSubmit={addUrl} class="unlock-admin-form">
                    <input
                      class="editor-input mono"
                      placeholder="https://example.com/page"
                      value={url()}
                      onInput={(event) => setUrl(event.currentTarget.value)}
                    />
                    <button
                      class="btn btn-primary"
                      type="submit"
                      {...adminGateProps(
                        action.busy() || !url().trim(),
                        "add URLs",
                      )}
                    >
                      Add page
                    </button>
                  </form>
                </div>
                <div class="panel-body">
                  <FormError message={action.error()} />
                  <Show
                    when={current().websites.length > 0}
                    fallback={
                      <div class="empty">
                        <div class="empty-title">No pages yet</div>
                        <div class="empty-hint">
                          Add a URL above to start an experiment.
                        </div>
                      </div>
                    }
                  >
                    <div class="pg-cards">
                      <For each={current().websites}>
                        {(site) => (
                          <A
                            class="pg-card"
                            href={`/playground/w/${site.websiteId}`}
                          >
                            <span class="pg-card-title mono">
                              {site.originUrl}
                            </span>
                            <span class="pg-card-meta">
                              Open experiment bench →
                            </span>
                          </A>
                        )}
                      </For>
                    </div>
                  </Show>
                </div>
              </section>

              <Show when={voiceOpen()}>
                <VoiceDrawer
                  clientId={props.clientId}
                  clientName={current().name}
                  onClose={() => setVoiceOpen(false)}
                />
              </Show>
            </>
          )}
        </Show>
      </div>
    </main>
  );
}
